import React, { useMemo, memo, useRef, useLayoutEffect } from "react";
import { useExcelReader } from "../../file-reader/useExcelReader";
import { CellHyperlinkValue, CellRichTextValue } from "exceljs";
import { cellIsHardNoTranslate, toggleCellFocus } from "./utils";
import {
  strictCellValueTypeIsHyperlink,
  strictCellValueTypeIsRichText,
  strictCellValueTypeIsString,
} from "./types";

const NO_TRANSLATE_CELL_ELEMENT = <span className="tw-text-gray-600">***</span>;

const StringCell: React.FC<{
  cellValue: string;
  isNoTranslate?: boolean;
  addNoTranslateText?: boolean;
}> = ({ cellValue, isNoTranslate, addNoTranslateText }) => {
  return (
    <>
      {cellValue}{" "}
      {isNoTranslate && addNoTranslateText && NO_TRANSLATE_CELL_ELEMENT}
    </>
  );
};

const RichTextCell: React.FC<{
  cellValue: CellRichTextValue;
  isNoTranslate?: boolean;
  addNoTranslateText?: boolean;
}> = ({ cellValue, isNoTranslate, addNoTranslateText }) => {
  const jsxElements = cellValue.richText.map((segment) => {
    let Element: keyof JSX.IntrinsicElements = "span";
    const style: React.CSSProperties = {};

    if (segment.font) {
      if (segment.font.italic) {
        Element = "em";
      }
      if (segment.font.bold) {
        Element = "b";
      }
      if (segment.font.name) {
        style.fontFamily = segment.font.name;
      }
      if (segment.font.color?.theme) {
        style.color = `#${segment.font.color.theme
          .toString(16)
          .padStart(6, "0")}`;
      }
    }

    return (
      <Element key={segment.text} style={style}>
        {segment.text}
      </Element>
    );
  });

  return (
    <>
      {jsxElements}
      {isNoTranslate && addNoTranslateText && NO_TRANSLATE_CELL_ELEMENT}
    </>
  );
};

const HyperlinkCell: React.FC<{
  cellValue: CellHyperlinkValue;
  isNoTranslate?: boolean;
  addNoTranslateText?: boolean;
}> = ({ cellValue, isNoTranslate, addNoTranslateText }) => {
  return (
    <>
      <a
        href={cellValue.hyperlink}
        target="_blank"
        rel="noopener noreferrer"
        title={cellValue.hyperlink}
      >
        {cellValue.text}
        {isNoTranslate && addNoTranslateText && NO_TRANSLATE_CELL_ELEMENT}
      </a>
    </>
  );
};

const AnyCell: React.FC<{
  cellValue: { text?: string };
  isNoTranslate?: boolean;
  addNoTranslateText?: boolean;
}> = ({ cellValue, isNoTranslate, addNoTranslateText }) => {
  return (
    <>
      {cellValue?.text || ""}{" "}
      {isNoTranslate && addNoTranslateText && NO_TRANSLATE_CELL_ELEMENT}
    </>
  );
};

type Props = {
  rowNumber: number;
  cellNumber: number;
  excelReader: ReturnType<typeof useExcelReader>;
  cellStyle?: React.CSSProperties;
  isNoTranslate?: boolean;
};

const CellRenderComponent: React.FC<Props> = ({
  rowNumber,
  cellNumber,
  excelReader,
  cellStyle = {},
  isNoTranslate = false,
}) => {
  const ref = useRef<HTMLTableCellElement>(null);

  useLayoutEffect(() => {
    if (ref.current && isNoTranslate) {
      ref.current.classList.remove(
        "txu-left",
        "txu-right",
        "txu-up",
        "txu-down"
      );
      const prevRow = rowNumber - 1;
      const prevCol = cellNumber - 1;
      const nextRow = rowNumber + 1;
      const nextCol = cellNumber + 1;
      const left = Boolean(
        document.querySelector(
          `[data-row="${rowNumber}"][data-column="${prevCol}"].txu-no-translate`
        )
      );
      const right = Boolean(
        document.querySelector(
          `[data-row="${rowNumber}"][data-column="${nextCol}"].txu-no-translate`
        )
      );
      const up = Boolean(
        document.querySelector(
          `[data-row="${prevRow}"][data-column="${cellNumber}"].txu-no-translate`
        )
      );
      const down = Boolean(
        document.querySelector(
          `[data-row="${nextRow}"][data-column="${cellNumber}"].txu-no-translate`
        )
      );

      if (left) {
        ref.current.classList.add("txu-left");
      }
      if (right) {
        ref.current.classList.add("txu-right");
      }
      if (up) {
        ref.current.classList.add("txu-up");
      }
      if (down) {
        ref.current.classList.add("txu-down");
      }
    }
  }, [rowNumber, cellNumber, isNoTranslate, ref.current]);

  const cell = useMemo(() => {
    return excelReader.getCell(rowNumber, cellNumber);
  }, [excelReader, rowNumber, cellNumber]);

  const cellBody = useMemo(() => {
    if (!cell) return "";
    const addNoTranslateText = !cellIsHardNoTranslate(cell);
    if (strictCellValueTypeIsString(cell)) {
      return (
        <StringCell
          cellValue={cell.value}
          isNoTranslate={isNoTranslate}
          addNoTranslateText={addNoTranslateText}
        />
      );
    }
    if (strictCellValueTypeIsRichText(cell)) {
      return (
        <RichTextCell
          cellValue={cell.value}
          isNoTranslate={isNoTranslate}
          addNoTranslateText={addNoTranslateText}
        />
      );
    }
    if (strictCellValueTypeIsHyperlink(cell)) {
      return (
        <HyperlinkCell
          cellValue={cell.value}
          isNoTranslate={isNoTranslate}
          addNoTranslateText={addNoTranslateText}
        />
      );
    }
    return (
      <AnyCell
        cellValue={cell.value as any}
        isNoTranslate={isNoTranslate}
        addNoTranslateText={addNoTranslateText}
      />
    );
  }, [cell, isNoTranslate]);

  return (
    <td
      ref={ref}
      style={cellStyle}
      data-row={rowNumber}
      data-column={cellNumber}
      tabIndex={cellNumber}
      onFocus={() => {
        toggleCellFocus(cellNumber, rowNumber, true);
      }}
      onBlur={() => {
        toggleCellFocus(cellNumber, rowNumber, false);
      }}
      className={`tw-truncate tw-max-w-16 ${
        isNoTranslate ? "txu-no-translate" : ""
      }`}
    >
      {cellBody}
    </td>
  );
};

export const CellRender = memo(CellRenderComponent, (prevProps, nextProps) => {
  return (
    prevProps.rowNumber === nextProps.rowNumber &&
    prevProps.cellNumber === nextProps.cellNumber &&
    prevProps.excelReader === nextProps.excelReader &&
    prevProps.isNoTranslate === nextProps.isNoTranslate
  );
});
